import React, { useEffect, useRef, useState } from 'react';
import MediaGalleryStage from './MediaGalleryStage';
import { createOwnerCameraArchiveApi } from '../../services/ownerCameraArchiveApi';

// Camera recordings of one canonical parent in the shared Media gallery
// (MediaGalleryStage, as uploaded attachments). Rows are the parent's ready
// videos and, once known, each video's recording parts. Bytes come only from
// the typed camera replay route under current authority (the opening tile's
// already-verified Blob is reused for its own row); Download saves that same
// Blob; owner Delete is the per-session camera tombstone. Every async result is
// fenced by account, selection and mount; owned object URLs are revoked.
export const GALLERY_DECODE_DEADLINE_MS = 20000;
const FAILURES = { request: 'Video unavailable. It could not be loaded.', decode: 'Video unavailable. This browser could not play it.',
  stalled: 'Video unavailable. It did not finish loading.' };
const DELETION = { pending: 'Video deleted. Erasure pending.', complete: 'Video deleted.' };
const keyOf = row => `${row.sid}|${row.part ? row.part.name : '*'}`;
const partRows = (sid, parts) => parts.map((part, partIndex) => ({ sid, part, partIndex, parts: parts.length }));

function initial(videos, startId, start) {
  const rows = videos.flatMap(v => (v.ref.id === startId && start?.parts?.length ? partRows(v.ref.id, start.parts) : [{ sid: v.ref.id, part: null, partIndex: 0, parts: null }]));
  const index = rows.findIndex(r => r.sid === startId && (!r.part || !start?.part || r.part.name === start.part));
  return { rows, index: Math.max(0, index) };
}

export default function CameraVideoGallery({ activity, videos, startId, start = null, backendService, identity, ownerId, isOwner = false, timeZone, onDeleted, close }) {
  const [seed] = useState(() => initial(videos, startId, start));
  const [rows, setRows] = useState(seed.rows);
  const [index, setIndex] = useState(seed.index);
  const [views, setViews] = useState({});
  const [attempt, setAttempt] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState(null);
  const viewsRef = useRef({});
  const owned = useRef(new Map());
  // A setup may claim the prefetched Blob once. If React retires that entire
  // setup (StrictMode replay or a same-owner service replacement), cleanup
  // releases the claim so the successor can use the still-valid bytes. Retry
  // inside one live setup fetches fresh authorized bytes instead.
  const startBlob = useRef(start?.blob && start.part ? { key: `${startId}|${start.part}`, blob: start.blob, claimedBy: null } : null);
  const scope = useRef(null);
  const selection = useRef(null);
  const retired = useRef(new Set());
  const closeRef = useRef(close); closeRef.current = close;

  const current = gallery => scope.current === gallery && !gallery.abort.signal.aborted
    && backendService.authenticatedIdentity === identity && backendService.positionOwner === ownerId;
  const setView = (key, view) => {
    const next = { ...viewsRef.current };
    if (view) next[key] = view; else delete next[key];
    viewsRef.current = next;
    setViews(next);
  };
  const release = key => {
    const url = owned.current.get(key);
    if (url) { URL.revokeObjectURL(url); owned.current.delete(key); }
  };
  // Account/login change: stop this exact setup and leave the gallery.
  useEffect(() => {
    const gallery = { abort: new AbortController() };
    const urls = owned.current;
    scope.current = gallery;
    const leave = () => {
      if (scope.current !== gallery) return;
      gallery.abort.abort();
      if (selection.current?.scope === gallery) selection.current.abort.abort();
      closeRef.current?.();
    };
    const off = backendService.onAccountScopeChanged?.(leave);
    return () => {
      off?.();
      gallery.abort.abort();
      if (selection.current?.scope === gallery) selection.current.abort.abort();
      urls.forEach(url => URL.revokeObjectURL(url));
      urls.clear();
      viewsRef.current = {};
      if (startBlob.current?.claimedBy === gallery) startBlob.current.claimedBy = null;
      if (scope.current === gallery) scope.current = null;
    };
  }, [backendService]);

  const row = rows[index];
  const key = row ? keyOf(row) : null;
  useEffect(() => {
    const gallery = scope.current;
    if (!row || !gallery || !current(gallery) || retired.current.has(row.sid)) return undefined;
    const existing = viewsRef.current[key];
    if (existing?.scope === gallery && (existing.status === 'ready' || existing.status === 'error')) return undefined;
    const abort = new AbortController();
    const mine = { abort, sid: row.sid, scope: gallery };
    selection.current = mine;
    const live = () => !abort.signal.aborted && !retired.current.has(row.sid) && current(gallery);
    setView(key, { status: 'loading', scope: gallery });
    (async () => {
      try {
        const api = createOwnerCameraArchiveApi({ backendService, identity, ownerId });
        if (!row.part) {
          const parts = await api.parts(row.sid, abort.signal);
          if (!live()) return;
          if (!parts.length) throw new Error('No recording parts');
          // The selected index now addresses this video's first part row.
          setRows(prev => prev.flatMap(r => (r.sid === row.sid && !r.part ? partRows(r.sid, parts) : [r])));
          return;
        }
        const seedBlob = startBlob.current;
        const hit = seedBlob?.key === key && seedBlob.claimedBy === null ? seedBlob.blob : null;
        if (hit) seedBlob.claimedBy = gallery;
        const blob = hit || await api.media(row.sid, row.part, abort.signal);
        if (!live()) return;
        release(key);
        const url = URL.createObjectURL(blob);
        if (!live()) { URL.revokeObjectURL(url); return; }
        owned.current.set(key, url);
        setView(key, { status: 'ready', url, decoded: false, scope: gallery });
      } catch {
        if (live()) setView(key, { status: 'error', failure: 'request', scope: gallery });
        else if (scope.current === gallery && !abort.signal.aborted && !retired.current.has(row.sid)) {
          gallery.abort.abort(); closeRef.current?.();
        }
      }
    })();
    return () => {
      abort.abort();
      if (selection.current === mine) selection.current = null;
    };
  }, [key, attempt, backendService]); // eslint-disable-line react-hooks/exhaustive-deps

  const view = key ? views[key] : null;
  const failDecode = (url, failure = 'decode') => {
    const entry = Object.entries(viewsRef.current).find(([, v]) => v.url === url);
    if (!entry || !current(entry[1].scope)) return;
    release(entry[0]);
    setView(entry[0], { status: 'error', failure, scope: entry[1].scope });
  };
  const failDecodeRef = useRef(failDecode); failDecodeRef.current = failDecode;
  useEffect(() => {
    if (view?.status !== 'ready' || view.decoded) return undefined;
    const timer = setTimeout(() => failDecodeRef.current(view.url, 'stalled'), GALLERY_DECODE_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [view?.status, view?.url, view?.decoded]);
  const decoded = url => {
    const entry = Object.entries(viewsRef.current).find(([, v]) => v.url === url);
    if (entry && current(entry[1].scope) && !entry[1].decoded) setView(entry[0], { ...entry[1], decoded: true });
  };
  const retry = () => {
    const gallery = scope.current;
    if (!gallery || !current(gallery) || !row || retired.current.has(row.sid)) return;
    release(key); setView(key, null); setAttempt(a => a + 1);
  };

  const download = () => {
    const gallery = scope.current;
    if (view?.status !== 'ready' || !gallery || !current(gallery) || retired.current.has(row.sid)) return;
    const a = document.createElement('a');
    a.href = view.url;
    a.download = row.part?.name || 'video.mp4';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const remove = async () => {
    if (!isOwner || !row || deleting) return;
    if (!window.confirm('Remove this attachment? This cannot be undone.')) return;
    const gallery = scope.current;
    if (!gallery || !current(gallery)) return;
    const sid = row.sid;
    setDeleting(true); setNotice(null);
    try {
      const outcome = await createOwnerCameraArchiveApi({ backendService, identity, ownerId }).remove(sid, gallery.abort.signal);
      if (!current(gallery)) return;
      if (outcome !== 'complete' && outcome !== 'pending') return;
      // The accepted tombstone retires this exact session synchronously, before
      // row state can commit. Its held continuation therefore cannot allocate
      // or retain a URL in the acknowledgement turn; siblings remain live.
      retired.current.add(sid);
      if (selection.current?.sid === sid) selection.current.abort.abort();
      [...owned.current.keys()].filter(k => k.startsWith(`${sid}|`)).forEach(release);
      const nextViews = Object.fromEntries(Object.entries(viewsRef.current).filter(([k]) => !k.startsWith(`${sid}|`)));
      viewsRef.current = nextViews; setViews(nextViews);
      if (startBlob.current?.key.startsWith(`${sid}|`)) startBlob.current = null;
      const remaining = rows.filter(r => r.sid !== sid);
      setRows(remaining);
      setIndex(i => Math.max(0, Math.min(i, remaining.length - 1)));
      setNotice({ text: DELETION[outcome], alert: false });
      onDeleted?.(sid, outcome);
      if (!remaining.length) closeRef.current?.();
    } catch {
      if (current(gallery)) setNotice({ text: 'Deletion outcome unknown. Retry.', alert: true });
    } finally { if (current(gallery)) setDeleting(false); }
  };

  const video = row && videos.find(v => v.ref.id === row.sid);
  const clock = value => new Intl.DateTimeFormat(undefined, { timeZone, timeStyle: 'short' }).format(new Date(value));
  // Server lifecycle times, labelled as such (not a first-frame timestamp).
  const broadcast = video && (video.live_since && video.ended_at ? `Broadcast ${clock(video.live_since)}–${clock(video.ended_at)}`
    : video.live_since ? `Broadcast start ${clock(video.live_since)}` : video.ended_at ? `Broadcast end ${clock(video.ended_at)}` : null);
  return (
    <MediaGalleryStage
      title={activity.name}
      date={video?.live_since || activity.created_at}
      timeZone={timeZone}
      details={[video?.device_name, broadcast, row?.parts > 1 ? `Part ${row.partIndex + 1} of ${row.parts}` : null]}
      kind="video"
      url={view?.status === 'ready' ? view.url : null}
      status={view?.status === 'ready' ? 'ready' : view?.status === 'error' ? 'error' : 'loading'}
      error={FAILURES[view?.failure] || FAILURES.request}
      onRetry={retry}
      index={index}
      count={rows.length}
      canPrevious={index > 0}
      canNext={index < rows.length - 1}
      onPrevious={() => { if (!deleting && index > 0) setIndex(index - 1); }}
      onNext={() => { if (!deleting && index < rows.length - 1) setIndex(index + 1); }}
      onClose={close}
      onDownload={download}
      onDelete={isOwner ? remove : null}
      busy={deleting}
      onVideoReady={decoded}
      onVideoError={failDecode}
      notices={<>
        {deleting && <p className="media-removal-progress" role="status">Deleting…</p>}
        {notice && <p className={notice.alert ? 'media-remove-error' : 'media-removal-progress'} role={notice.alert ? 'alert' : 'status'}>{notice.text}</p>}
      </>}
    />
  );
}
